import { useEffect, useState } from 'react'
import { NotificationType, UserNotificationPreference, UserPreference } from 'common/types'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { usePreferenceApi } from 'hooks/use-preference-api/usePreferenceApi'
import { useThemeSwitch } from 'hooks/theme'
import { useAnalytics } from 'hooks/use-analytics/useAnalytics'



export const usePreference = (preferenceType?: string) => {
  const { backendUser, backendPartner } = useAuth()
  const { fetchPreferenceByUser, createPreference, updatePreference, createUserNotifications, fetchNotificationTypes, fetchAllUserNotificationPreferenceById } = usePreferenceApi()
  const [enabled, setEnabled] = useState(false)
  const [preferenceSettings, setPreferenceSettings] = useState<UserPreference>()
  const [showMode, setShowMode] = useState<boolean>()
  const [editedPreference, setEditedPreference] = useState<UserPreference>()
  const [needToCreatePreference, setNeedToCreatePreference] = useState<boolean>(false)
  const { mode } = useThemeSwitch()
  const { logEventAnalytics } = useAnalytics()
  const [notificationTypes, setNotificationTypes] = useState<NotificationType[]>([])
  const [userNotificationPreferences, setUserNotificationPreferences] = useState<UserNotificationPreference[]>()
  const [notificationsPreference, setNotificationsPreference] = useState<UserNotificationPreference>()

  const handleTracker = (event: string) => {
    const partnerLog = {}
    const userLog = {}
    userLog['User ID'] = backendUser?.id
    if (backendUser?.partnerId) {
      partnerLog['Partner ID'] = backendPartner?.id
      partnerLog['Company Name'] = backendPartner?.name
      partnerLog['Account Type'] = 'Partner'
    } else {
      userLog['Account Type'] = backendUser?.role
    }

    logEventAnalytics(
      backendUser?.partnerId ? 'Partner' : backendUser?.role,
      event,
      'mixpanel',
      event,
      {
        'Email notifications switch': editedPreference.emailNotifications,
        'Inapp notifications switch': editedPreference.inAppNotifications,
        'Dark mode switch': editedPreference.darkMode,
        ...userLog,
        ...partnerLog
      },
      backendUser?.id,
      {
        $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
        $email: backendUser?.username
      }
    )
  }

  const createNewPreference = async (newPreference: UserPreference) => {
    if (backendUser) {
      const response = await createPreference(newPreference)
      if (response) {
        setPreferenceSettings(response)
        setNeedToCreatePreference(false)
        handleTracker('Preferences updated')
      }
    }
  }

  const createUserNotification = async (newPreference: UserNotificationPreference) => {
    if (backendUser) {
      const response = await createUserNotifications(newPreference)
      if (response) {
        setPreferenceSettings(response)
        setNeedToCreatePreference(false)
        handleTracker('Preferences updated')
      }
    }
  }

  const getAllUserNotificationPreferences = async () => {
    const response = await fetchAllUserNotificationPreferenceById(backendUser.id)   
    if (response) {
      setUserNotificationPreferences(response)
    }
  }

  

  useEffect(() => {
    const getPreference = async () => {
      if (backendUser) {
        const response = await fetchPreferenceByUser(backendUser.id)
        if (response) {
          setPreferenceSettings({ ...response })
          setEditedPreference({ ...response })
          setNeedToCreatePreference(false)
        } else {
          const defaultPreference = {
            userId: backendUser.id,
            emailNotifications: true,
            darkMode: mode !== 'dark' ? true : false
          }
          setPreferenceSettings({ ...defaultPreference })
          setEditedPreference({ ...defaultPreference })
          setNeedToCreatePreference(true)
        }
      }
    }
    getPreference()
    setShowMode(preferenceType === 'partner' ? false : true)

    const getNotificationTypes = async () => {
      if (backendUser) {
        const response:NotificationType[] = await fetchNotificationTypes()
        if(response) {
          setNotificationTypes(response)
        }
      }
    }
    getNotificationTypes()
  }, [])

  const updatePreferenceSettings = async (editedPreference: UserPreference) => {
    await updatePreference(editedPreference)
    setPreferenceSettings(editedPreference)
    handleTracker('Preferences updated')
  }

  return {
    backendUser,
    preferenceEnabled: enabled,
    setPreferenceEnabled: setEnabled,
    preferenceSettings,
    setPreferenceSettings,
    editedPreference,
    setEditedPreference,
    updatePreferenceSettings,
    needToCreatePreference,
    setNeedToCreatePreference,
    createNewPreference,
    showMode,
    createUserNotification,
    notificationTypes,
    userNotificationPreferences,
    setUserNotificationPreferences,
    getAllUserNotificationPreferences,
    notificationsPreference,
    setNotificationsPreference,
  } as const
}
